/**
 * Follow-ups and the feed — `activities.html`'s `#followups`/feed sections.
 *
 * Both read real data. Follow-ups is `useFollowUps.js` against
 * `modules/followups` (§8) — its own table, kept separate from `crm.activity`
 * so a missed follow-up can be re-planned without erasing the record that it
 * was missed. The feed has no endpoint of its own to read: `docs/API.md` §8
 * says outright that `GET /activities` *is* the activity feed, so this calls
 * `useActivities` the same way the main Activities screen's own feed panel
 * does, sorted recent-first — see `ActivitiesPage.jsx`.
 *
 * Follow-ups is a List/Calendar/Follow-ups toggle on `ActivitiesPage.jsx` now,
 * not its own route — the same move already made for Calendar and for
 * Pipeline/Opportunities before it. `FollowUpsView` has no `PageHeader` of its
 * own for the same reason `CalendarView` doesn't: embedding one would put a
 * second `<h1>` on the page. The feed stays its own routed screen (`FeedPage`,
 * `/feed`) — nobody asked to fold that one in too, and it has a real,
 * separate reason to exist as a dedicated page (the chat-style infinite
 * scroll wants the full viewport).
 */
import { useMemo, useState } from 'react';
import {
  PageHeader, Card, Badge, Avatar, MockBadge, Spinner, ErrorState, EmptyState,
} from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { isMock } from '../../api/mockFallback.js';
import { dueLabel, relativeTime, number } from '../../utils/formatters.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useActivitiesFeed } from './useActivities.js';
import { useFollowUps, useCompleteFollowUp, useSnoozeFollowUp } from './useFollowUps.js';
import { KIND_META, KIND_LABEL, relatedRecordLinks } from './ActivityTimeline.jsx';
import { feedVerb, FeedFilterSeg, useFeedScroll } from './feedFormat.jsx';

/* -------------------------------------------------------------------------- */
/* Follow-ups                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Follow-ups that are due or overdue.
 *
 * Sorted by how overdue, worst first — this screen exists to answer "what have I
 * let slip", and sorting by date buries the two-week-old one under today's.
 */
export function FollowUpsView() {
  const { can } = useAuth();
  const canEdit = can('followups', 'edit');
  const [snoozeTarget, setSnoozeTarget] = useState(null);

  const { data, isLoading, error, refetch } = useFollowUps({});
  const complete = useCompleteFollowUp();
  const snooze = useSnoozeFollowUp();

  const rows = [...(data?.data ?? [])].sort((a, b) => (b.days_overdue ?? 0) - (a.days_overdue ?? 0));
  const counts = data?.counts;

  return (
    <>
      <Card
        title={(
          <>
            Follow-ups
            {isMock(data) && <MockBadge section="follow-ups" />}
          </>
        )}
        subtitle={counts ? `${counts.overdue} overdue · ${counts.today} due today · ${counts.this_week} this week` : undefined}
      >
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : rows.length === 0 ? (
          <EmptyState icon="bi-check2-circle" title="All caught up" message="Every follow-up is on schedule." />
        ) : (
          <ul className="list-x">
            {rows.map((fu) => {
              const related = relatedRecordLinks(fu.related);
              const relatedText = related.length ? related.map((r) => r.text).join(', ') : '—';
              return (
                <li key={fu.id} className="list-x-item">
                  <Avatar name={fu.assigned_to?.full_name ?? '?'} size={34} />
                  <div className="flex-grow-1 min-w-0">
                    <div className="fw-600 t-sm text-truncate">{relatedText}</div>
                    <div className="t-xs text-muted text-truncate">{fu.note ?? 'No note'}</div>
                  </div>
                  {fu.channel && <Badge tone="neutral">{fu.channel}</Badge>}
                  <Badge tone={fu.status === 'overdue' ? 'rose' : fu.status === 'done' ? 'emerald' : 'sky'}>
                    {fu.status === 'overdue' ? `Overdue ${fu.days_overdue}d` : fu.status === 'done' ? 'Done' : dueLabel(fu.due_at)}
                  </Badge>
                  <span className="t-xs text-muted d-none d-md-inline">{fu.assigned_to?.full_name ?? '—'}</span>
                  {canEdit && fu.status !== 'done' && (
                    <div className="d-flex gap-1">
                      <button type="button" className="btn-x btn-ghost btn-sm-x" onClick={() => setSnoozeTarget(fu)}>
                        Snooze
                      </button>
                      <button
                        type="button" className="btn-x btn-ghost btn-sm-x"
                        disabled={complete.isPending}
                        onClick={() => complete.mutate(fu.id)}
                      >
                        <i className="bi bi-check2 me-1" aria-hidden="true" />Done
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <SnoozeModal
        followUp={snoozeTarget}
        busy={snooze.isPending}
        onClose={() => setSnoozeTarget(null)}
        onConfirm={(payload) => snooze.mutate(
          { id: snoozeTarget.id, payload },
          { onSuccess: () => setSnoozeTarget(null) },
        )}
      />
    </>
  );
}

function SnoozeModal({ followUp, busy, onClose, onConfirm }) {
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');

  if (!followUp) return null;
  return (
    <div className="modal-x-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-x" role="dialog" aria-modal="true" aria-label="Snooze follow-up">
        <header className="modal-x-head">
          <h2 className="card-x-title mb-0">Snooze follow-up</h2>
          <button type="button" className="btn-close btn-sm" onClick={onClose} aria-label="Close" />
        </header>
        <div className="modal-x-body">
          <div className="field-x mb-2">
            <label className="field-x-label">New due date</label>
            <input type="datetime-local" className="form-control" value={until} onChange={(e) => setUntil(e.target.value)} autoFocus />
          </div>
          <div className="field-x">
            <label className="field-x-label">Reason</label>
            <input className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer on leave" />
          </div>
        </div>
        <footer className="modal-x-foot">
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn btn-save btn-sm-x" disabled={!until || busy}
            onClick={() => onConfirm({ until: new Date(until).toISOString(), reason: reason || undefined })}
          >
            {busy ? <><ButtonSpinner />Saving…</> : 'Snooze'}
          </button>
        </footer>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Feed                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The team's activity stream — real `crm.activity` rows, not a separate feed
 * resource. This is the same `#feed` card `activities.html` renders (title,
 * `All/Mentions/Mine` toggle, `.tl` timeline, comment composer), given its
 * own route instead of a hash anchor — see the file-level doc comment above.
 *
 * Reads chat-style, oldest at top / newest at bottom, 25 rows to start and 20
 * more each time the list is scrolled to its top — see `useActivitiesFeed`
 * for the cursor paging and `useFeedScroll` (`feedFormat.jsx`) for the
 * scroll-position bookkeeping, shared with the Activities screen's own
 * embedded `TeamFeedCard` (10 and 10 there — a sidebar card, not a full page).
 */
export function FeedPage() {
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const params = useMemo(() => ({
    owner_id: filter === 'mine' ? user?.id : undefined,
    created_after: dateFrom ? new Date(`${dateFrom}T00:00:00`).toISOString() : undefined,
    /* End of the selected day, not the start — a `to` date should include what happened on it. */
    created_before: dateTo ? new Date(`${dateTo}T23:59:59.999`).toISOString() : undefined,
  }), [filter, user?.id, dateFrom, dateTo]);

  const {
    data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useActivitiesFeed(params);

  /* Pages arrive newest-page-first (each page itself newest-to-oldest); flatten
     then reverse once so the whole list reads oldest → newest, top → bottom. */
  const rows = useMemo(
    () => [...(data?.pages ?? [])].flatMap((page) => page.data ?? []).reverse(),
    [data],
  );

  const { scrollRef, onScroll, resetScroll } = useFeedScroll(rows, { hasNextPage, isFetchingNextPage, fetchNextPage });

  /* Every page carries the same `page.total`/`estimated` for this filter —
     grab it off whichever page loaded first rather than recomputing. */
  const feedPage = data?.pages?.[0]?.page;

  return (
    <>
      <PageHeader
        title="Activity feed"
        subtitle="What the team has logged, oldest to newest."
        badge={isMock(data) ? <MockBadge section="feed" /> : null}
      />
      <Card
        bodyClass="p-0"
        title="Team activity feed"
        actions={<FeedFilterSeg value={filter} onChange={(v) => { setFilter(v); resetScroll(); }} />}
        footer={(
          <div className="t-xs ink-3">
            Showing {number(rows.length)}
            {feedPage?.total != null && (
              <> of {feedPage.estimated ? 'about ' : ''}{number(feedPage.total)}</>
            )}
          </div>
        )}
      >
        <div className="filter-bar">
          <label className="filter-x">
            <span className="filter-x-label">From</span>
            <input
              type="date" className="form-control form-control-sm" value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => { setDateFrom(e.target.value); resetScroll(); }}
            />
          </label>
          <label className="filter-x">
            <span className="filter-x-label">To</span>
            <input
              type="date" className="form-control form-control-sm" value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => { setDateTo(e.target.value); resetScroll(); }}
            />
          </label>
          {(dateFrom || dateTo) && (
            <button
              type="button" className="btn-x btn-ghost btn-sm-x"
              onClick={() => { setDateFrom(''); setDateTo(''); resetScroll(); }}
            >
              Clear dates
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="p-4"><Spinner /></div>
        ) : error ? (
          <div className="p-4"><ErrorState error={error} onRetry={refetch} /></div>
        ) : rows.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon="bi-clock-history" title="Nothing here"
              message={dateFrom || dateTo ? 'No activity in that date range.' : 'Activity will show up here as soon as something is logged.'}
            />
          </div>
        ) : (
          <div ref={scrollRef} onScroll={onScroll} className="p-3" style={{ maxHeight: 560, overflowY: 'auto' }}>
            {isFetchingNextPage && (
              <div className="d-flex justify-content-center py-2 t-xs text-muted"><ButtonSpinner />Loading older…</div>
            )}
            {!hasNextPage && !isFetchingNextPage && (
              <div className="text-center t-2xs ink-4 pb-2">Start of the feed</div>
            )}
            <div className="tl">
              {rows.map((item) => {
                const [icon, color] = KIND_META[item.kind] ?? ['bi-circle', 'var(--ink-3)'];
                const { verb } = feedVerb(item);
                const related = relatedRecordLinks(item.related);
                const when = item.completed_at ?? item.created_at;
                return (
                  <div key={item.id} className="tl-item">
                    <div className="tl-dot" style={{ background: color }}><i className={`bi ${icon}`} aria-hidden="true" /></div>
                    <div className="tl-title">
                      <span className="fw-600">{item.owner?.full_name ?? 'Someone'}</span> {verb} {(KIND_LABEL[item.kind] ?? item.kind).toLowerCase()}: {item.subject}
                    </div>
                    <div className="tl-meta">
                      {related.length ? related.map((r) => r.text).join(', ') : 'No related record'} · {relativeTime(when)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </>
  );
}
